import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import Page from './routes/Page';
import { useCommunity } from "./context/community";
import { setConfiguration } from 'react-grid-system';
import { usePopupNotification } from 'context/PopupNotificationContext';
import { Toaster } from 'react-hot-toast';
import { useTelegramAutoLogin } from './hooks/useTelegramAutoLogin';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { usePermissions } from './context/permissions';
import Loader from 'components/Loader/Loader';
import { getToken } from './services/auth';
import { PAGES } from './routes/PAGES';
import { useLocation } from 'react-router-dom';
import { isKrasavaLandingEnabled } from 'pages/Welcome/components/KrasavaLanding/parseKrasavaLandingConfig';
import ScrollToTop from './routes/ScrollToTop';
import { getTelegramStartParamContext, TELEGRAM_MANUAL_LOGIN_INTENT_KEY, JOIN_START_PARAM_PREFIX, resolveTelegramStartParamRedirect, isTelegramMiniAppEnvironment, isTelegramStartParamTargetReached } from './services/telegramStartParam';

const RedirectSuccess = lazy(() => import('components/RedirectSuccess/RedirectSuccess'));
const LazySentry = lazy(() => import('LazySentry'));
const isProd = process.env.NODE_ENV === 'production';
const TELEGRAM_START_REDIRECT_KEY = 'telegramStartRedirectPath';
/** Once user has landed on start_param target, do not hijack SPA navigation (effect re-runs while Telegram/sessionStorage still expose start_param). */
const TELEGRAM_START_PARAM_CONSUMED_KEY = 'telegramStartParamRedirectConsumed';
const COMMUNITY_OPTION_PUBLIC_EVENTS_PAGE = 'PUBLIC_EVENTS_PAGE';
const CHAT_WIDGET_API_HOST = 'chat.newo.ai';
const CHAT_WIDGET_UNAUTHORIZED_STATUS = 401;
const AUTH_PAGES = [
    PAGES.LOGIN,
    PAGES.REGISTRATION,
    PAGES.RECOVERY,
    PAGES.CONFIRM_RECOVERY,
    PAGES.ONE_TIME_CODE,
    PAGES.AUTH_MAGIC,
];

const decodeStartParam = (rawStartParam) => {
    if (!rawStartParam || typeof rawStartParam !== 'string') {
        return '';
    }
    try {
        return decodeURIComponent(rawStartParam);
    } catch (error) {
        return rawStartParam;
    }
};

const hasCommunityOption = (communityOptions, optionName) => {
    if (!Array.isArray(communityOptions)) {
        return false;
    }
    return communityOptions.includes(optionName);
};

const getResponseLikeObject = (reason) => {
    if (!reason || typeof reason !== 'object') {
        return null;
    }

    const candidate = reason?.response && typeof reason.response === 'object'
        ? reason.response
        : reason;
    const hasStatus = typeof candidate.status === 'number';
    const hasUrl = typeof candidate.url === 'string';

    return hasStatus && hasUrl ? candidate : null;
};

const isChatWidgetUnauthorizedResponse = (reason) => {
    const response = getResponseLikeObject(reason);
    if (!response) {
        return false;
    }
    if (response.status !== CHAT_WIDGET_UNAUTHORIZED_STATUS) {
        return false;
    }
    try {
        const hostname = response.url ? new URL(response.url).hostname : '';
        return hostname.includes(CHAT_WIDGET_API_HOST);
    } catch {
        return false;
    }
};

const readStartParamConsumed = (normalizedStartParam, redirectPath) => {
    const raw = sessionStorage.getItem(TELEGRAM_START_PARAM_CONSUMED_KEY);
    if (!raw) {
        return false;
    }
    try {
        const consumed = JSON.parse(raw);
        return consumed.startParam === normalizedStartParam && consumed.redirectPath === redirectPath;
    } catch {
        sessionStorage.removeItem(TELEGRAM_START_PARAM_CONSUMED_KEY);
        return false;
    }
};

const markStartParamConsumed = (normalizedStartParam, redirectPath) => {
    sessionStorage.setItem(
        TELEGRAM_START_PARAM_CONSUMED_KEY,
        JSON.stringify({ startParam: normalizedStartParam, redirectPath }),
    );
};

setConfiguration({
    maxScreenClass: 'xl',
    containerWidths: [540, 740, 960, 1280, 1540, 1810],
});

const App = () => {
    const { loading, community, updateCurrentUser } = useCommunity();
    const { domain, options: communityOptions } = community;
    const location = useLocation();
    const krasavaBoot = isKrasavaLandingEnabled(community);
    const { showNotification, hideNotification } = usePopupNotification();
    const notificationKey = useRef(null);
    const setTimer = useState(null)[1];

    // Handle Telegram mini-app auto-login
    // This runs before Page.js checks for token, allowing auto-login to set token first
    const { isChecking: isTelegramChecking, isAuthenticated, hasAttempted: hasTelegramAttempted } = useTelegramAutoLogin();
    const { isTelegramWebApp: isTelegramWebAppFromHook } = useTelegramWebApp();
    const { refreshPermissions } = usePermissions();
    const isTelegramMiniApp = isTelegramMiniAppEnvironment();

    // Wait for auto-login only inside a real Mini App. The Telegram SDK script always
    // defines window.Telegram.WebApp, so SDK presence alone would freeze the browser UI.
    const isTelegramWebApp = isTelegramWebAppFromHook || isTelegramMiniApp;

    // Wait for Telegram auto-login check to complete before rendering Page
    // This prevents race condition where Page checks token before auto-login finishes
    const [isTelegramCheckComplete, setIsTelegramCheckComplete] = useState(!isTelegramWebApp);

    useEffect(() => {
        if (!isTelegramWebApp) {
            setIsTelegramCheckComplete(true);
            return;
        }

        if (isTelegramChecking || !hasTelegramAttempted) {
            setIsTelegramCheckComplete(false);
            return;
        }

        let cancelled = false;
        const finishTelegramBootstrap = async () => {
            if (isAuthenticated) {
                try {
                    await refreshPermissions();
                    if (updateCurrentUser) {
                        await updateCurrentUser();
                    }
                } catch (err) {
                    console.warn('[App] Failed to refresh after Telegram login:', err);
                }
            }
            if (!cancelled) {
                setIsTelegramCheckComplete(true);
            }
        };

        finishTelegramBootstrap();
        return () => {
            cancelled = true;
        };
    }, [isTelegramChecking, isTelegramWebApp, hasTelegramAttempted, isAuthenticated, refreshPermissions]); // eslint-disable-line react-hooks/exhaustive-deps -- updateCurrentUser is recreated each render

    // Handle start_param after Telegram auth check.
    // Persist redirect target so first launch does not lose deep-link intent while auth/community bootstrap runs.
    useEffect(() => {
        if (!isTelegramWebApp || !isTelegramCheckComplete) {
            return;
        }

        try {
            const { startParam, hasLiveStartParam } = getTelegramStartParamContext();

            console.log('[App] Checking start_param (after Telegram auth):', startParam);
            if (!startParam) {
                return;
            }

            const redirectPath = resolveTelegramStartParamRedirect(startParam);
            if (!redirectPath) {
                sessionStorage.removeItem(TELEGRAM_START_REDIRECT_KEY);
                return;
            }

            const normalizedStartParam = decodeStartParam(startParam).trim();
            const isJoinStartParam = normalizedStartParam.startsWith(JOIN_START_PARAM_PREFIX);
            const hasPublicEventsPage = hasCommunityOption(communityOptions, COMMUNITY_OPTION_PUBLIC_EVENTS_PAGE);

            if (process.env.NODE_ENV === 'development') {
                console.log('[App][start_param debug]', {
                    startParam,
                    redirectPath,
                    tokenPresent: !!getToken(),
                    currentPath: `${window.location.pathname}${window.location.search}`,
                });
            }

            // Keep deep-link target for login/onboarding flow as well.
            sessionStorage.setItem(TELEGRAM_START_REDIRECT_KEY, redirectPath);
            sessionStorage.setItem('redirectToAfterLogin', redirectPath);

            const currentPath = window.location.pathname;
            const isOnAuthPage = AUTH_PAGES.includes(currentPath);
            const hasManualLoginIntent = sessionStorage.getItem(TELEGRAM_MANUAL_LOGIN_INTENT_KEY) === 'true';

            if (getToken() || !isOnAuthPage) {
                sessionStorage.removeItem(TELEGRAM_MANUAL_LOGIN_INTENT_KEY);
            }

            // Allow anonymous redirect for Telegram join links only when community enables public events.
            if (!getToken()) {
                // Respect explicit "Log in" action from the event page.
                // Without this flag, Telegram can keep providing start_param and force redirect back to event.
                if (isOnAuthPage && hasManualLoginIntent) {
                    return;
                }
                // If user is on auth page without manual login intent and without fresh start_param, stay on auth page.
                if (isOnAuthPage && !hasLiveStartParam) return;

                if (isJoinStartParam && hasPublicEventsPage) {
                    const currentPathWithSearch = `${window.location.pathname}${window.location.search}`;
                    if (isTelegramStartParamTargetReached(currentPathWithSearch, redirectPath)) {
                        markStartParamConsumed(normalizedStartParam, redirectPath);
                    } else if (!readStartParamConsumed(normalizedStartParam, redirectPath)) {
                        window.location.replace(redirectPath);
                    }
                }
                return;
            }

            if (readStartParamConsumed(normalizedStartParam, redirectPath)) {
                return;
            }

            const currentPathWithSearch = `${window.location.pathname}${window.location.search}`;
            if (!isTelegramStartParamTargetReached(currentPathWithSearch, redirectPath)) {
                console.log('[App] Redirecting by start_param:', redirectPath);
                window.location.replace(redirectPath);
            } else {
                sessionStorage.removeItem(TELEGRAM_START_REDIRECT_KEY);
                markStartParamConsumed(normalizedStartParam, redirectPath);
            }
        } catch (error) {
            console.error('[App] Error handling start_param:', error);
        }
    }, [isTelegramWebApp, isTelegramCheckComplete, isAuthenticated, communityOptions]);

    useEffect(() => {
        if (!isProd) { window.addEventListener('error', handleNetworkError); }
        window.addEventListener('offline', handleNetworkError);
        window.addEventListener('online', handleOnline);

        return () => {
            if (!isProd) { window.removeEventListener('error', handleNetworkError); }
            window.removeEventListener('offline', handleNetworkError);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    useEffect(() => {
        const handleWidgetUnhandledRejection = (event) => {
            const reason = event?.reason;
            if (!isChatWidgetUnauthorizedResponse(reason)) {
                return;
            }

            // Prevent React dev overlay for known third-party widget auth mismatch.
            event.preventDefault();
            event.stopPropagation();
            if (!isProd) {
                const response = getResponseLikeObject(reason);
                console.warn('[App] Ignored chat widget unauthorized rejection', response?.url || '');
            }
        };

        const handleWidgetErrorEvent = (event) => {
            const errorObject = event?.error || event?.reason;
            const fromUnauthorizedResponse = isChatWidgetUnauthorizedResponse(errorObject);
            const fromResponseMessage = typeof event?.message === 'string' && event.message.includes('[object Response]');

            if (!fromUnauthorizedResponse && !fromResponseMessage) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            if (!isProd) {
                console.warn('[App] Ignored chat widget runtime error event', event?.message || '');
            }
        };

        window.addEventListener('unhandledrejection', handleWidgetUnhandledRejection, true);
        window.addEventListener('error', handleWidgetErrorEvent, true);
        return () => {
            window.removeEventListener('unhandledrejection', handleWidgetUnhandledRejection, true);
            window.removeEventListener('error', handleWidgetErrorEvent, true);
        };
    }, []);

    useEffect(() => {
        const onWelcome = location.pathname === PAGES.WELCOME;
        if (krasavaBoot && onWelcome) {
            document.documentElement.setAttribute('data-krasava-boot', '');
        } else {
            document.documentElement.removeAttribute('data-krasava-boot');
        }
    }, [krasavaBoot, location.pathname]);

    useEffect(() => {
        let loader = document.querySelector("#video_loader");
        if (loader) {
            const isEventPage = window.location.pathname.startsWith('/event/')
                && !window.location.pathname.startsWith('/event/create');
            if (loading && isEventPage) {
                loader.style.display = 'none';
                loader.style.opacity = 0;
            } else if (loading) {
                setTimer(prevTimer => {
                    prevTimer && clearTimeout(prevTimer);
                    return null;
                })
                loader.style.display = "flex";
                loader.style.opacity = 1;
            } else {
                if (domain === 'annamoney' && process.env.REACT_APP_GA_ANNAMONEY_ID) {
                    gtag('config', process.env.REACT_APP_GA_ANNAMONEY_ID);
                }

                if (domain === 'enjoyhannover' && process.env.REACT_APP_GA_ENJOYHANNOVER_ID) {
                    gtag('config', process.env.REACT_APP_GA_ENJOYHANNOVER_ID);
                }

                if (domain === 'columbiaalumni' && process.env.REACT_APP_GA_COLUMBIAALUMNI_ID) {
                    gtag('config', process.env.REACT_APP_GA_COLUMBIAALUMNI_ID);
                }

                if (domain === 'sfeducation' && process.env.REACT_APP_GA_SFEDUCAION_ID) {
                    gtag('config', process.env.REACT_APP_GA_SFEDUCAION_ID);
                }

                if (domain === 'passeio' && process.env.REACT_APP_GA_PASSEIO_ID) {
                    gtag('config', process.env.REACT_APP_GA_PASSEIO_ID);
                }

                gtag('event', 'loaded', {
                    community: domain
                })
                loader.style.opacity = 0;
                setTimer(setTimeout(() => {
                    loader.remove();
                }, 50))
            }
        }
    }, [loading, setTimer, domain]);


    const handleNetworkError = (event) => {
        if (notificationKey.current) {
            return;
        }
        const message = navigator.onLine
            ? 'Something is broken. Try to reload the page '
            : 'The app is offline. Check your connection ';

        const title = navigator.onLine
            ? 'Oops!'
            : 'Offline';
        const key = showNotification(title, message, 'default', 500000);
        notificationKey.current = key;
    };

    const handleOnline = () => {
        if (notificationKey.current) {
            hideNotification(notificationKey.current);
            notificationKey.current = null;
        }
    };

    // Show loader while Telegram auto-login is checking
    if (isTelegramWebApp && !isTelegramCheckComplete) {
        return (
            <>
                <Loader />
                <Toaster
                    position="top-right"
                    toastOptions={{
                        duration: 3000,
                        style: {
                            background: '#363636',
                            color: '#fff',
                        },
                        success: {
                            duration: 3000,
                            style: {
                                background: '#10B981',
                            },
                        },
                        error: {
                            duration: 4000,
                            style: {
                                background: '#EF4444',
                            },
                        },
                        warning: {
                            duration: 4000,
                            style: {
                                background: '#F59E0B',
                                color: '#000',
                            },
                        },
                    }}
                />
            </>
        );
    }

    return (
        <>
            <ScrollToTop />
            <Page />
            <Suspense fallback={<></>}>
                <RedirectSuccess domain={domain} />
            </Suspense>
            <Suspense fallback={<></>}>
                <LazySentry />
            </Suspense>
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 3000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        style: {
                            background: '#10B981',
                        },
                    },
                    error: {
                        duration: 4000,
                        style: {
                            background: '#EF4444',
                        },
                    },
                    warning: {
                        duration: 4000,
                        style: {
                            background: '#F59E0B',
                            color: '#000',
                        },
                    },
                }}
            />
        </>
    )
};

export default App
